import React, { useState, useEffect } from "react";
import { QualitativeResult, QualExcerpt, QualCode, QualTheme, ThemeRelationship, ConnectedAgent, analyzeTranscript, getQualitativeResult } from "../api";

interface Props {
  sessionId?: string;
  transcript?: string;
  qualResult?: QualitativeResult | null;
}

const AGENT_ICONS: Record<string, React.ReactElement> = {
  search: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  database: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
  ),
  brain: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 2a7 7 0 017 7c0 2.5-1.5 4.5-3 6l-4 5-4-5c-1.5-1.5-3-3.5-3-6a7 7 0 017-7z" />
    </svg>
  ),
  globe: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
    </svg>
  ),
  shield: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
};

export default function QualitativeTab({ sessionId, transcript, qualResult: externalResult }: Props) {
  const [activeSection, setActiveSection] = useState<"codes" | "themes" | "transcript" | "agents">("themes");
  const [highlightedCode, setHighlightedCode] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Real data from backend
  const [result, setResult] = useState<QualitativeResult | null>(externalResult || null);

  // Load from external result prop
  useEffect(() => {
    if (externalResult) {
      setResult(externalResult);
    }
  }, [externalResult]);

  // Auto-load if we have a sessionId but no result
  useEffect(() => {
    if (sessionId && !result && !loading) {
      setLoading(true);
      getQualitativeResult(sessionId)
        .then(res => {
          setResult(res.data);
          setError(null);
        })
        .catch(() => {
          // No stored result — if we have a transcript, analyze it
          if (transcript) {
            runAnalysis(transcript);
          } else {
            setError("No analysis found. Run an interview first to generate qualitative data.");
            setLoading(false);
          }
        });
    }
  }, [sessionId]);

  const runAnalysis = async (text: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await analyzeTranscript(text, sessionId || "");
      setResult(res.data);
    } catch (err) {
      console.error("Analysis error:", err);
      setError("Failed to run qualitative analysis. Make sure the backend is running on port 8000.");
    } finally {
      setLoading(false);
    }
  };

  const excerpts: QualExcerpt[] = result?.excerpts || [];
  const codes: QualCode[] = result?.codes || [];
  const themes: QualTheme[] = result?.themes || [];
  const themeRelationships: ThemeRelationship[] = result?.theme_relationships || [];
  const connectedAgents: ConnectedAgent[] = result?.connected_agents || [];
  const summary = result?.summary || "";

  const sentimentColor = (s: string) =>
    s === "positive" ? "#27AE60" : s === "cautionary" ? "#E67E22" : s === "negative" ? "#E74C3C" : "rgba(0,0,0,0.4)";

  // Empty state
  if (!result && !loading) {
    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 40 }}>
        <div style={{ fontSize: 40, marginBottom: 16, opacity: 0.15 }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="1.5" strokeLinecap="round">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
            <polyline points="14,2 14,8 20,8" />
            <line x1="8" y1="13" x2="16" y2="13" />
            <line x1="8" y1="17" x2="13" y2="17" />
          </svg>
        </div>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: "#000", margin: "0 0 8px" }}>No Qualitative Data Yet</h3>
        <p style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", textAlign: "center", maxWidth: 400, lineHeight: 1.6, margin: 0 }}>
          Start an expert elicitation interview in the Home tab chat. After a few exchanges, click "Extract Variables" to run the full analysis pipeline — thematic coding, sentiment, and connected agents will appear here.
        </p>
        {error && (
          <p style={{ fontSize: 11, color: "#E74C3C", marginTop: 12 }}>{error}</p>
        )}
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 20, height: 20, border: "2px solid rgba(0,0,0,0.1)",
            borderTop: "2px solid #000", borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }} />
          <span style={{ fontSize: 13, color: "rgba(0,0,0,0.5)" }}>Running qualitative analysis...</span>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "20px 32px 0", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: "#000" }}>
            Qualitative Analysis
          </h2>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{
              fontSize: 10, fontWeight: 600, color: "#27AE60",
              padding: "2px 8px", borderRadius: 100, background: "rgba(39,174,96,0.1)",
            }}>
              Analysis complete
            </span>
          </div>
        </div>
        {summary && (
          <p style={{ fontSize: 12, fontWeight: 300, color: "#000", margin: "0 0 16px", lineHeight: 1.6 }}>
            {summary}
          </p>
        )}

        {/* Section tabs */}
        <div style={{ display: "flex", gap: 2, borderBottom: "1px solid rgba(0,0,0,0.06)", marginBottom: 0 }}>
          {(["themes", "codes", "transcript", "agents"] as const).map(section => (
            <button
              key={section}
              onClick={() => setActiveSection(section)}
              style={{
                padding: "8px 16px", fontSize: 12, fontWeight: activeSection === section ? 600 : 400,
                color: "#000", background: "none", border: "none", cursor: "pointer",
                borderBottom: activeSection === section ? "2px solid #000" : "2px solid transparent",
                marginBottom: -1, textTransform: "capitalize",
              }}
            >
              {section === "agents" ? "Connected Agents" : section}
              {section === "codes" && (
                <span style={{ marginLeft: 6, fontSize: 10, opacity: 0.4 }}>{codes.length}</span>
              )}
              {section === "themes" && (
                <span style={{ marginLeft: 6, fontSize: 10, opacity: 0.4 }}>{themes.length}</span>
              )}
              {section === "agents" && (
                <span style={{ marginLeft: 6, fontSize: 10, opacity: 0.4 }}>{connectedAgents.length}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 32px 32px" }}>

        {/* THEMES */}
        {activeSection === "themes" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {themes.map((theme, i) => (
              <div key={theme.name} className="animate-in" style={{
                background: "#fff", borderRadius: 14, padding: "20px 24px",
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
                animationDelay: `${i * 0.05}s`, animationFillMode: "backwards",
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "rgba(0,0,0,0.25)" }}>
                      THEME {String(i + 1).padStart(2, "0")}
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#000" }}>{theme.name}</span>
                  </div>
                  <span style={{
                    fontSize: 9, fontWeight: 600, textTransform: "uppercase",
                    color: sentimentColor(theme.sentiment), letterSpacing: "0.05em",
                  }}>
                    {theme.sentiment}
                  </span>
                </div>
                <p style={{ fontSize: 12, color: "rgba(0,0,0,0.6)", margin: "0 0 14px", lineHeight: 1.6 }}>
                  {theme.description}
                </p>

                {/* Saturation bar */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: 10, color: "rgba(0,0,0,0.4)", fontWeight: 500, minWidth: 60 }}>Saturation</span>
                  <div style={{ flex: 1, height: 4, borderRadius: 2, background: "rgba(0,0,0,0.06)" }}>
                    <div style={{
                      width: `${theme.saturation * 100}%`, height: "100%", borderRadius: 2,
                      background: theme.saturation > 0.7 ? "#000" : "rgba(0,0,0,0.3)",
                      transition: "width 0.5s ease",
                    }} />
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 600, color: "rgba(0,0,0,0.5)", minWidth: 30, textAlign: "right" }}>
                    {(theme.saturation * 100).toFixed(0)}%
                  </span>
                </div>

                {/* Code chips */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {theme.codes.map(codeName => {
                    const code = codes.find(c => c.label === codeName);
                    return (
                      <span key={codeName} style={{
                        padding: "3px 10px", borderRadius: 100, fontSize: 10, fontWeight: 500,
                        background: code ? `${code.color}15` : "rgba(0,0,0,0.04)",
                        color: code?.color ?? "#000",
                        border: `1px solid ${code ? `${code.color}30` : "rgba(0,0,0,0.08)"}`,
                      }}>
                        {codeName}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Theme relationships */}
            {themeRelationships.length > 0 && (
              <div style={{
                background: "#fff", borderRadius: 14, padding: "20px 24px",
                border: "1px solid rgba(0,0,0,0.06)",
              }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: "rgba(0,0,0,0.4)", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                  Theme Relationships
                </span>
                <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
                  {themeRelationships.map((rel, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11 }}>
                      <span style={{ fontWeight: 600, color: "#000", minWidth: 140 }}>{rel.from}</span>
                      <span style={{
                        padding: "2px 8px", borderRadius: 100, fontSize: 9, fontWeight: 500,
                        background: "rgba(0,0,0,0.04)", color: "rgba(0,0,0,0.5)",
                      }}>
                        {rel.relation}
                      </span>
                      <div style={{ flex: 1, height: 1, background: `rgba(0,0,0,${rel.strength * 0.2})` }} />
                      <span style={{ fontWeight: 500, color: "rgba(0,0,0,0.6)", minWidth: 120, textAlign: "right" }}>{rel.to}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* CODES */}
        {activeSection === "codes" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {codes.map((code, i) => (
              <div
                key={code.id}
                className="animate-in"
                style={{
                  background: highlightedCode === code.id ? `${code.color}08` : "#fff",
                  borderRadius: 10, padding: "14px 18px",
                  border: `1px solid ${highlightedCode === code.id ? `${code.color}30` : "rgba(0,0,0,0.06)"}`,
                  cursor: "pointer", transition: "all 0.15s ease",
                  animationDelay: `${i * 0.03}s`, animationFillMode: "backwards",
                }}
                onClick={() => setHighlightedCode(highlightedCode === code.id ? null : code.id)}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: "50%", background: code.color, flexShrink: 0,
                    }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#000" }}>{code.label}</span>
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 500, color: "rgba(0,0,0,0.4)",
                    padding: "2px 8px", borderRadius: 100, background: "rgba(0,0,0,0.04)",
                  }}>
                    {code.count} references
                  </span>
                </div>

                {/* Show excerpts when expanded */}
                {highlightedCode === code.id && (
                  <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                    {code.excerpt_indices.map(idx => {
                      const excerpt = excerpts[idx];
                      if (!excerpt) return null;
                      return (
                        <div key={idx} style={{
                          padding: "10px 14px", borderRadius: 8,
                          background: `${code.color}08`,
                          borderLeft: `3px solid ${code.color}`,
                          fontSize: 11, color: "rgba(0,0,0,0.7)", lineHeight: 1.6,
                        }}>
                          <span style={{ fontWeight: 600, color: "#000" }}>{excerpt.speaker}</span>
                          <span style={{ color: "rgba(0,0,0,0.3)", marginLeft: 6, fontSize: 10 }}>{excerpt.time}</span>
                          <br />
                          "{excerpt.text}"
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* TRANSCRIPT */}
        {activeSection === "transcript" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {excerpts.map((excerpt, i) => {
              const isInterviewer = excerpt.speaker === "Interviewer";
              const appliedCodes = codes.filter(c => c.excerpt_indices.includes(i));
              return (
                <div key={i} className="animate-in" style={{
                  padding: "14px 0", borderBottom: "1px solid rgba(0,0,0,0.04)",
                  animationDelay: `${i * 0.03}s`, animationFillMode: "backwards",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span style={{
                      fontSize: 11, fontWeight: isInterviewer ? 500 : 700,
                      color: isInterviewer ? "rgba(0,0,0,0.5)" : "#000",
                    }}>
                      {excerpt.speaker}
                    </span>
                    <span style={{ fontSize: 10, color: "rgba(0,0,0,0.25)" }}>{excerpt.time}</span>
                    {appliedCodes.length > 0 && (
                      <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
                        {appliedCodes.map(c => (
                          <span key={c.id} style={{
                            width: 6, height: 6, borderRadius: "50%", background: c.color,
                          }} title={c.label} />
                        ))}
                      </div>
                    )}
                  </div>
                  <p style={{
                    margin: 0, fontSize: 12, lineHeight: 1.7,
                    color: isInterviewer ? "rgba(0,0,0,0.5)" : "rgba(0,0,0,0.75)",
                    fontStyle: isInterviewer ? "italic" : "normal",
                  }}>
                    {excerpt.text}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {/* CONNECTED AGENTS */}
        {activeSection === "agents" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <p style={{ fontSize: 11, color: "rgba(0,0,0,0.5)", margin: "0 0 8px", lineHeight: 1.6 }}>
              Tacit automatically connects to external data sources and AI agents to enrich your qualitative analysis.
            </p>
            {connectedAgents.map((agent, i) => (
              <div key={agent.name} className="animate-in" style={{
                background: "#fff", borderRadius: 12, padding: "16px 20px",
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0 1px 8px rgba(0,0,0,0.02)",
                animationDelay: `${i * 0.05}s`, animationFillMode: "backwards",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                    background: agent.status === "active" ? "rgba(0,0,0,0.04)" : "rgba(0,0,0,0.02)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: agent.status === "active" ? "#000" : "rgba(0,0,0,0.3)",
                  }}>
                    {AGENT_ICONS[agent.icon] || AGENT_ICONS.search}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#000" }}>{agent.name}</span>
                      <span style={{
                        fontSize: 9, fontWeight: 600, textTransform: "uppercase",
                        padding: "2px 6px", borderRadius: 100, letterSpacing: "0.05em",
                        background: agent.status === "active" ? "rgba(39,174,96,0.1)" : agent.status === "complete" ? "rgba(0,0,0,0.04)" : "rgba(0,0,0,0.03)",
                        color: agent.status === "active" ? "#27AE60" : "rgba(0,0,0,0.35)",
                      }}>
                        {agent.status === "active" && (
                          <span style={{
                            display: "inline-block", width: 5, height: 5, borderRadius: "50%",
                            background: "#27AE60", marginRight: 4, verticalAlign: "middle",
                          }} />
                        )}
                        {agent.status}
                      </span>
                    </div>
                    <p style={{ margin: "4px 0 0", fontSize: 11, color: "rgba(0,0,0,0.5)", lineHeight: 1.4 }}>
                      {agent.desc}
                    </p>
                  </div>
                  {agent.matches !== null && (
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 16, fontWeight: 300, color: "#000" }}>
                        {agent.matches.toLocaleString()}
                      </div>
                      <div style={{ fontSize: 9, color: "rgba(0,0,0,0.35)" }}>matches</div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Available integrations */}
            <div style={{
              marginTop: 8, padding: "16px 20px", borderRadius: 12,
              border: "1px dashed rgba(0,0,0,0.12)", background: "rgba(0,0,0,0.01)",
            }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: "rgba(0,0,0,0.35)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Available Integrations
              </span>
              <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
                {["Notion Research DB", "Zotero Library", "Slack #research", "Google Scholar API", "Qualtrics Surveys", "Atlas.ti Import"].map(name => (
                  <span key={name} style={{
                    padding: "5px 12px", borderRadius: 100, fontSize: 10, fontWeight: 500,
                    background: "rgba(0,0,0,0.03)", color: "rgba(0,0,0,0.4)",
                    border: "1px solid rgba(0,0,0,0.06)", cursor: "pointer",
                  }}>
                    + {name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
